import { mdiCheck, mdiClose, mdiPlayCircle } from '@mdi/js'
import Icon from '@mdi/react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { taskUrls, taskVideoUrls } from 'routes/trial/components/TrialTasksGroup'
import { TrialTask } from 'routes/trial/interfaces'
import { setTrialWidgetHidden } from 'services/ui/actions'
import { isTrialWidgetHiddenSelector } from 'services/ui/selectors'
import { fetchTrainingVideoSections, getTrialTasks } from 'services/users/actions'
import { currentUserSelector, getStartedTasksSelector, isTrialActiveSelector } from 'services/users/selectors'
import { StoreThunkDispatch } from 'store/state'
import styles from './TrialWidget.pcss'

const POPUP_ELEVATION = 8

export function TrialWidget() {
  const navigate = useNavigate()
  const isHidden = useSelector(isTrialWidgetHiddenSelector)
  const user = useSelector(currentUserSelector)
  const isTrial = useSelector(isTrialActiveSelector)
  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const tasks = useSelector(getStartedTasksSelector)
  const completedTasks = tasks.filter(t => t.completed)
  const showWidget = !isHidden && completedTasks.length < tasks.length && !location.pathname.startsWith('/onboarding')
  const showCloseBtn = !isTrial && user?.account.hasPaymentInfo
  const [loading, setLoading] = useState(tasks.length === 0)
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (location.state?.intro) {
      setAnchor(buttonRef.current)
    }
  }, [location.state?.intro])

  const fetchTasks = useCallback(() => {
    Promise.all([
      dispatch(getTrialTasks()).unwrap(),
      dispatch(fetchTrainingVideoSections()).unwrap()
    ])
      .catch((error) => {
        console.log('Failed to fetch trial info!', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dispatch])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const hideWidget = (e: any) => {
    e.stopPropagation()
    dispatch(setTrialWidgetHidden(true))
  }

  if (!showWidget) {
    return null
  }

  const openPopup = () => {
    setAnchor(buttonRef.current)
    fetchTasks()
  }

  const closePopup = () => {
    setAnchor(null)
  }

  const onClick = (code: string) => {
    const url = taskUrls[code]
    navigate(url)
  }

  // eslint-disable-next-line no-magic-numbers
  const completed = tasks.length > 0 ? Math.round((completedTasks.length / tasks.length) * 100) : 0

  return (
    <Fragment>
      {!loading && (
        <div className={styles.wrapper}>
          <div className={styles.btn} ref={buttonRef} onClick={openPopup}>
            <span className={styles.badge}></span>
            {showCloseBtn && (
              <span className={styles.close} role="button" onClick={hideWidget}>
                <Icon path={mdiClose} size="14px" />
              </span>
            )}
            <span>Get Started</span>
          </div>
        </div>
      )}
      <Popper
        open={Boolean(anchor)}
        anchorEl={anchor}
        placement="top"
        className={styles.popper}
      >
        <ClickAwayListener onClickAway={closePopup}>
          <Paper classes={{ root: styles.paper }} elevation={POPUP_ELEVATION}>
            <div className={styles['paper-inner']}>
              <div className={styles.header}>
                <h2 className={styles.title}>{`${tasks.length} steps to get started`}</h2>
                <div className={styles['progress-box']}>
                  <span>{`${completed}%`}</span>
                  <span className={styles.progress}>
                    <div className={styles.bar} style={{ width: `${completed}%` }}></div>
                  </span>
                </div>
              </div>
              <div className={styles.content}>
                {tasks.map((t, index) => (
                  <TaskRow
                    key={t.code}
                    task={t}
                    order={index + 1}
                    active={location.pathname.startsWith(taskUrls[t.code])}
                    onClick={onClick}
                  />
                ))}
              </div>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Fragment>
  )
}

interface TaskRowProps {
  task: TrialTask
  active?: boolean
  order: number
  // onVideoClick?: (url: string) => void
  onClick: (code: string) => void
}

export function TaskRow({ task, active, order, onClick }: TaskRowProps) {
  const click = () => {
    onClick(task.code)
  }

  const onPlayClick = () => {
    // e.preventDefault()
    // e.stopPropagation()
    // if (onVideoClick) {
    //   onVideoClick(task.videoUrl || '')
    // }
    window.open(taskVideoUrls[task.code], '_blank')
  }

  return (
    <div
      data-testid="task-row"
      className={`${styles['task-box']} ${active ? styles.active : ''}`}
      key={task.code}
      onClick={click}
    >
      <div className={`${styles.number} ${task.completed ? styles.done : ''}`}>
        {task.completed ? <Icon path={mdiCheck} size="20px" /> : order}
      </div>
      <div className={styles.right}>
        <div className={styles.taskTitle}>
          {task.title || <FormattedMessage id={`trial-page.group-title.${task.code}`} values={{ value: task.maxValue }} />}
        </div>
        <div className={styles.subtitle}>
          <FormattedMessage id={`trial-page.group-subtitle.${task.code}`} />
        </div>
        <span className={styles['btn-play']} onClick={onPlayClick}>
          <Icon path={mdiPlayCircle} size="20px" />
          <span>Watch video</span>
        </span>
      </div>
    </div>
  )
}
